import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useAuth } from '../../contexts/auth';
import { useBreadcrumbs } from '../../contexts/breadcrumbsContext';
import { useTranslation } from '../../contexts/translation';
import TasksForm, { TaskInputsNew } from '../../forms/tasks/TasksForm';
import useQueryParams from '../../hooks/useQueryParams';
import { PageContentWrapper } from '../../layouts/contentWrapper/PageContentWrapper';
import { DetailPageParams } from '../../types/pageParams.types';
import { Task } from '../../types/task.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { swrConfig } from '../Assets/AssetsEdit';

export type TaskNewState = {
  defaultDocumentTypeId?: number;
};

interface TasksNewQueryParams {
  AssetLevel?: string | string[];
  ParentAssetLevel?: string | string[];
}

/**
 * Query param může přijít jako jedna hodnota (string) i vícekrát (string[]) - viz useQueryParams.
 */
const toNumberArray = (value?: string | string[]): number[] =>
  value ? ([] as string[]).concat(value).map(Number) : [];

export function TasksNew() {
  const [defaultFormData, setDefaultFormData] = useState<TaskInputsNew>({});
  const { user } = useAuth();
  const { assetClass } = useAssetClass();
  const { translate } = useTranslation();
  const transFormName = 'Task.Create';
  const { id } = useParams<DetailPageParams>();
  const { setValue } = useBreadcrumbs();
  const { state }: { state: TaskNewState | undefined } = useLocation();
  const queryParams: TasksNewQueryParams = useQueryParams();
  const assetLevelIds = toNumberArray(queryParams.AssetLevel);
  const parentAssetLevelIds = toNumberArray(queryParams.ParentAssetLevel);

  useEffect(() => {
    setValue({
      pageName: 'New task',
      steps: [
        { name: 'Home', pathToCutCount: 3 },
        { name: 'Tasks', pathToCutCount: 2 },
      ],
    });
    return () => {
      setValue(null);
    };
  }, []);

  /**
   * Načte task podle id.
   */
  const { data: task } = useSWR<Task>(
    id ? `${API_URL.TASK_ITEM}?id=${id}` : '',
    fetcher,
    swrConfig,
  );

  // Právo na založení tasku
  const { data: documentTypes } = useSWR<DocumentType[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClass}&taskCreateDesktop=true&visibleOnly=true&validateForUser=true`,
    fetcher,
    swrConfig,
  );

  /**
   * Po načtení tasku je nutné nastavit defaultní hodnoty formuláře.
   */
  useEffect(() => {
    if (!task) return;
    setDefaultFormData({
      ...task,
      assetTopLevelParenId: task.assetTopLevelParenId,
    });
  }, [task]);

  if (!documentTypes) return <Loader />;
  if (documentTypes.length === 0) return <>Access forbidden</>;

  return (
    <PageContentWrapper
      heading={translate!('New task', transFormName)}
      loading={false}
      error={false}
    >
      <TasksForm
        isReadOnly={false}
        defaultFormData={defaultFormData}
        transFormName={'Task.Create'}
        isCopy={!!task}
        isEdit={false}
        assetLevelIds={assetLevelIds}
        parentAssetLevelIds={parentAssetLevelIds}
        defaultDocumentTypeId={state?.defaultDocumentTypeId}
      />
    </PageContentWrapper>
  );
}
